import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import styles from './quality-metric-prop-box.module.scss';
import { colorPalette } from 'shared/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { TrendIndicator } from '../trend-indicator';

/**
 * MetricPropBox is a component that displays a box with a label, value, and percentage.
 *
 * @export
 * @function MetricPropBox
 * @param {Object} props
 * @param {string} [props.label] - The label for the metric box.
 * @param {string} [props.value] - The value to display in the box.
 * @param {string} props.percentage - The percentage to display in the box.
 * @param {string} [props.boxBg] - The background color for the box.
 * @param {number} [props.minimumRecords] - The minimum number of records required for the metric.
 * @param {number} [props.totalRecords] - The total number of records for the metric.
 * @param {boolean} [props.isSuccess=false] - Indicates if the
 * @param {boolean} [props.showIcon=true] - Whether to show the success/failure icon.
 * @return {*}
 */
export function MetricPropBox({
  label,
  value,
  percentage,
  boxBg,
  minimumRecords,
  totalRecords,
  isSuccess = false,
  showIcon = true,
  trend,
}) {
  const lang = useSelector(getLang('QUALITY_METRIC'));

  const hasInsufficientData = minimumRecords && totalRecords < minimumRecords;

  let boxColor = boxBg;

  if (totalRecords) {
    if (hasInsufficientData) {
      boxColor = colorPalette.neutral3;
    } else if (isSuccess) {
      boxColor = colorPalette.greenPositive;
    } else {
      boxColor = colorPalette.redNegative;
    }
  }

  return (
    <div className={styles.metricPropBox}>
      {label && <label>{label}</label>}
      <div
        style={{ backgroundColor: boxColor }}
        className={classNames(styles.percentageBox, {
          [styles.insufficientDataBox]: hasInsufficientData,
        })}>
        <div className={styles.percentage}>
          {!hasInsufficientData && showIcon && (
            <FontAwesomeIcon
              color={colorPalette.neutral1}
              icon={isSuccess ? faCheck : faTriangleExclamation}
            />
          )}
          <p>{hasInsufficientData ? lang.NO_DATA : percentage}</p>
        </div>
        {!hasInsufficientData && trend && <TrendIndicator trend={trend} />}
      </div>
      {value && (
        <label className={styles.valueLabel}>
          {hasInsufficientData ? lang.NO_LOGGED_CASES : value}
        </label>
      )}
    </div>
  );
}
